import { useEffect, useMemo, useRef, useState } from "react";
import { Calculator, Check, ArrowRight, Minus, Plus, Flame, Lock, Star, Clock } from "lucide-react";
import PackageCompare from "./PackageCompare";
import { useSite } from "@/context/SiteContext";
import { api, fmt } from "@/lib/api";

function Pages({ c, pages, setPages }) {
  const step = (d) => setPages((p) => Math.min(c.max_pages, Math.max(1, p + d)));
  return (
    <div>
      <div className="flex items-center justify-between gap-4">
        <p className="font-semibold">Səhifələrin sayı</p>
        <div className="flex items-center gap-2">
          <button type="button" className="calc-step" onClick={() => step(-1)} aria-label="Azalt" data-testid="calc-pages-minus"><Minus size={14} /></button>
          <span className="w-10 text-center font-display text-xl font-bold" data-testid="calc-pages-value">{pages}</span>
          <button type="button" className="calc-step" onClick={() => step(1)} aria-label="Artır" data-testid="calc-pages-plus"><Plus size={14} /></button>
        </div>
      </div>
      <input type="range" min={1} max={c.max_pages} value={pages} onChange={(e) => setPages(Number(e.target.value))}
        className="calc-range w-full mt-4" style={{ "--p": `${((pages - 1) / Math.max(1, c.max_pages - 1)) * 100}%` }} aria-label="Səhifələrin sayı" data-testid="calc-pages-range" />
      <p className="text-xs text-[#77736B] mt-2">{c.included_pages} səhifə qiymətə daxildir, hər əlavə səhifə +{fmt(c.page_price)} ₼</p>
    </div>
  );
}

function Includes({ text, price, title }) {
  const items = (text || "").split("\n").map((x) => x.trim()).filter(Boolean);
  if (!items.length) return null;
  return (
    <div className="rounded-xl bg-[#F7F3EB] p-5 mb-8" data-testid="calc-base-includes">
      <p className="font-semibold text-sm">{title}{price != null && ` — ${fmt(price)} ₼`} <span className="font-normal text-[#77736B]">qiymətə daxildir:</span></p>
      <ul className="grid sm:grid-cols-2 gap-x-4 gap-y-2 mt-3">
        {items.map((x, i) => (
          <li key={i} className="flex items-start gap-2 text-sm"><Check size={15} className="text-[#b58a3f] mt-0.5 shrink-0" strokeWidth={2.5} />{x}</li>
        ))}
      </ul>
    </div>
  );
}

export function offerActive(s) {
  return !!s?.offer_enabled && (!s.offer_ends_at || new Date(s.offer_ends_at).getTime() > Date.now());
}

function Packages({ list, active, onPick }) {
  if (!list?.length) return null;
  return (
    <div className="mb-8">
      <p className="font-semibold">Hazır paketlər</p>
      <div className="grid sm:grid-cols-3 gap-3 mt-4">
        {list.map((p, i) => (
          <button type="button" key={p.name} onClick={() => onPick(p)} aria-pressed={active === p.name} data-testid={`calc-package-${i}`}
            className={`calc-opt !block text-left relative ${active === p.name ? "on pkg-on" : p.popular ? "pkg-popular" : ""}`}>
            {active === p.name && <span className="absolute top-3 right-3 w-5 h-5 rounded-full bg-[#D6AD62] text-[#111110] flex items-center justify-center" data-testid={`calc-package-selected-${i}`}><Check size={12} strokeWidth={3} /></span>}
            {p.popular && <span className="absolute -top-2.5 left-3 inline-flex items-center gap-1 rounded-full bg-[#2F5D50] text-white px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide" data-testid={`calc-package-popular-${i}`}><Star size={10} fill="currentColor" /> Ən populyar</span>}
            <span className="block font-display font-semibold">{p.name}</span>
            {p.desc && <span className="block text-xs text-[#77736B] mt-1 leading-snug">{p.desc}</span>}
            <span className="block text-xs text-[#b58a3f] mt-2 font-semibold">{p.pages} səhifə · {p.features.length} funksiya</span>
            {p.duration && <span className="flex items-center gap-1 text-[11px] text-[#2d2c29] mt-1" data-testid={`calc-package-duration-${i}`}><Clock size={11} /> Hazır olma: {p.duration}</span>}
            {p.free?.length > 0 && <span className="block text-[11px] text-[#3d7a37] mt-1 font-semibold">Pulsuz: {p.free.map((n) => n.split(" (")[0]).join(", ")}</span>}
          </button>
        ))}
      </div>
    </div>
  );
}

const sid = () => {
  let v = sessionStorage.getItem("lm_calc_sid");
  if (!v) { v = crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2)}`; sessionStorage.setItem("lm_calc_sid", v); }
  return v;
};

function useTrack(pages, picked, pkg) {
  const first = useRef(true);
  useEffect(() => {
    if (first.current) { first.current = false; return; }
    const t = setTimeout(() => api.post("/calc/track", { session_id: sid(), pages, features: picked, package: pkg }).catch(() => {}), 1500);
    return () => clearTimeout(t);
  }, [pages, picked, pkg]);
}

function Prepay({ total, pct }) {
  if (!(pct > 0 && pct < 100)) return null;
  const first = Math.round((total * pct) / 100);
  return (
    <div className="mt-3 grid grid-cols-2 gap-2 text-xs" data-testid="calc-prepay">
      <div className="rounded-lg bg-white/5 px-3 py-2"><p className="text-[#a9a398]">{pct}% başlanğıcda</p><p className="text-[#F3EEE4] font-semibold text-sm mt-0.5" data-testid="calc-prepay-first">{fmt(first)} ₼</p></div>
      <div className="rounded-lg bg-white/5 px-3 py-2"><p className="text-[#a9a398]">{100 - pct}% təhvil zamanı</p><p className="text-[#F3EEE4] font-semibold text-sm mt-0.5" data-testid="calc-prepay-second">{fmt(total - first)} ₼</p></div>
    </div>
  );
}

const per = (e) => (e.period ? ` / ${e.period}` : "");
const yearly = (list) => (list || []).reduce((s, e) => s + e.price * (/ay/i.test(e.period || "") ? 12 : 1), 0);

function Extras({ list }) {
  if (!list?.length) return null;
  return (
    <div className="mt-8" data-testid="calc-extras">
      <p className="font-semibold">Ayrıca ödənilir</p>
      <p className="text-xs text-[#77736B] mt-1">Bu xərclər saytın qiymətinə daxil deyil.</p>
      <div className="grid sm:grid-cols-2 gap-3 mt-4">
        {list.map((e, i) => (
          <div key={e.name} className="flex items-start gap-3 rounded-[14px] border border-dashed border-[#171715]/20 bg-[#F7F3EB] px-4 py-3.5" data-testid={`calc-extra-${i}`}>
            <span className="flex-1 min-w-0">
              <span className="block text-sm font-medium">{e.name}</span>
              {e.desc && <span className="block text-xs text-[#77736B] mt-1 leading-snug">{e.desc}</span>}
            </span>
            <span className="text-sm font-semibold whitespace-nowrap">{fmt(e.price)} ₼{per(e)}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function PriceCalculator() {
  const { config } = useSite();
  const c = config.calc;
  const [pages, setPages] = useState(c?.included_pages || 3);
  const [picked, setPicked] = useState([]);
  const [pkg, setPkg] = useState("");
  useTrack(pages, picked, pkg);
  const pkgObj = (c?.packages || []).find((p) => p.name === pkg);
  const locked = pkgObj?.features || [];
  const free = pkgObj?.free || [];
  const promo = offerActive(config.contacts) && c?.promo_base_price > 0 && c.promo_base_price < c.base_price;
  const full = useMemo(() => {
    if (!c) return 0;
    const extra = Math.max(0, pages - c.included_pages) * c.page_price;
    return c.base_price + extra + c.features.filter((f) => picked.includes(f.name) && !free.includes(f.name)).reduce((s, f) => s + f.price, 0);
  }, [c, pages, picked, free]);
  if (!c?.enabled) return null;
  const total = promo ? full - c.base_price + c.promo_base_price : full;
  const basePrice = promo ? c.promo_base_price : c.base_price;
  const showBase = !pkgObj || locked.every((f) => free.includes(f));
  const pickPkg = (p) => {
    if (p.name === pkg) { setPkg(""); setPicked([]); setPages(c.included_pages); return; }
    setPkg(p.name); setPages(p.pages); setPicked((cur) => [...new Set([...p.features, ...cur.filter((x) => !locked.includes(x))])]);
  };
  const toggle = (n) => !locked.includes(n) && setPicked((p) => (p.includes(n) ? p.filter((x) => x !== n) : [...p, n]));
  const apply = () => {
    const lines = [pkg && `Paket: ${pkg}`, `Kalkulyator: ${pages} səhifə`, ...c.features.filter((f) => picked.includes(f.name)).map((f) => `+ ${f.name} (${free.includes(f.name) ? "pulsuz" : `${fmt(f.price)} ₼`})`), `Təxmini qiymət: ${fmt(total)} ₼${promo ? ` (aksiya, əvvəl ${fmt(full)} ₼)` : ""}`,
      pkgObj?.duration && `Hazır olma müddəti: ${pkgObj.duration}`,
      c.extras?.length && `İlk il cəmi (domen + server ilə): ${fmt(total + yearly(c.extras))} ₼`,
      ...(c.extras || []).map((e) => `Ayrıca: ${e.name} — ${fmt(e.price)} ₼${per(e)}`)].filter(Boolean);
    window.dispatchEvent(new CustomEvent("lm:prefill", { detail: { service: "Veb sayt", message: lines.join("\n"), calc: { pages, features: picked, package: pkg }, calcSession: sid() } }));
    document.getElementById("contact")?.scrollIntoView({ behavior: "smooth" });
  };
  const extraCount = picked.filter((x) => !locked.includes(x)).length;

  return (
    <section id="calculator" data-testid="calculator-section" className="py-20 md:py-24 scroll-mt-16">
      <div className="container-lm grid lg:grid-cols-[1fr_1.5fr] gap-10 lg:gap-16 items-start">
        <div className="lg:sticky lg:top-28">
          <p className="eyebrow">Qiymət kalkulyatoru</p>
          <h2 className="font-display text-3xl sm:text-4xl font-semibold mt-4">Saytınız neçəyə başa gələcək?</h2>
          <p className="text-sm text-[#77736B] mt-4 max-w-sm leading-relaxed">Səhifələrin sayını və lazım olan funksiyaları seçin — təxmini qiyməti dərhal görün. Dəqiq qiymət layihə müzakirəsindən sonra təsdiqlənir.</p>
          <div className="calc-total mt-8" data-testid="calc-total-card">
            {promo && (
              <p className="inline-flex items-center gap-1.5 rounded-full bg-[#D6AD62] text-[#111110] px-3 py-1 text-xs font-bold mb-4" data-testid="calc-promo-badge">
                <Flame size={13} /> Aksiya: baza qiymət {fmt(c.promo_base_price)} ₼
              </p>
            )}
            <p className="text-xs uppercase tracking-[.18em] text-[#a9a398]">Təxmini qiymət</p>
            <p className="font-display text-5xl font-bold text-[#F3D592] mt-2" data-testid="calc-total"><span key={total} className="calc-num">{fmt(total)}</span> <span className="manat">₼</span></p>
            {promo && (
              <p className="text-sm mt-1" data-testid="calc-promo-saving">
                <s className="text-[#a9a398]" data-testid="calc-full-total">{fmt(full)} ₼</s>
                <span className="ml-2 text-[#9fd49a] font-semibold">Qənaət: {fmt(full - total)} ₼</span>
              </p>
            )}
            <p className="text-xs text-[#a9a398] mt-2" data-testid="calc-summary">
              {pkg && <b className="text-[#F3EEE4]">{pkg} · </b>}
              {showBase && <span data-testid="calc-summary-base">Baza qiymət {fmt(basePrice)} ₼ · </span>}
              {pages} səhifə · {extraCount} əlavə
            </p>
            {pkgObj?.duration && <p className="flex items-center gap-1.5 text-xs text-[#cfc9bd] mt-2" data-testid="calc-duration"><Clock size={12} /> Təxmini hazır olma müddəti: <b className="text-[#F3EEE4]">{pkgObj.duration}</b></p>}
            <Prepay total={total} pct={c.prepay_percent} />
            {c.extras?.length > 0 && (
              <div className="mt-4 border-t border-white/10 pt-3" data-testid="calc-first-year">
                <p className="flex items-baseline justify-between gap-3 text-sm">
                  <span className="text-[#cfc9bd]">İlk il cəmi (domen + server ilə)</span>
                  <b className="text-[#F3EEE4] whitespace-nowrap" data-testid="calc-first-year-total">{fmt(total + yearly(c.extras))} ₼</b>
                </p>
                <p className="text-[11px] text-[#a9a398] mt-1" data-testid="calc-extras-note">
                  Sayt {fmt(total)} ₼ + {c.extras.map((e) => `${e.name.split(" (")[0].toLowerCase()} ${/ay/i.test(e.period || "") ? `${fmt(e.price)} ₼ × 12` : `${fmt(e.price)} ₼`}`).join(" + ")}
                </p>
              </div>
            )}
            <button type="button" onClick={apply} className="btn-gold w-full mt-6 !rounded-xl" data-testid="calc-apply-btn">Bu qiymətlə müraciət et <ArrowRight size={16} /></button>
          </div>
        </div>
        <div className="min-w-0 rounded-2xl bg-white/70 border border-[#171715]/5 p-6 sm:p-8">
          <div className="flex items-center gap-3 mb-6"><span className="w-10 h-10 rounded-xl bg-[#171715] text-[#D6AD62] flex items-center justify-center"><Calculator size={18} /></span><p className="font-display font-semibold text-lg">Layihənizi qurun</p></div>
          <Packages list={c.packages} active={pkg} onPick={pickPkg} />
          <PackageCompare c={c} promo={promo} active={pkg} onPick={pickPkg} />
          <Includes text={c.base_includes} title={pkgObj && !showBase ? "Hər saytda" : "Baza paket"} price={showBase ? basePrice : null} />
          <Pages c={c} pages={pages} setPages={setPages} />
          <p className="font-semibold mt-8">{pkgObj ? "Funksiyalar" : "Əlavə funksiyalar"}</p>
          <div className="grid sm:grid-cols-2 gap-3 mt-4">
            {c.features.map((f, i) => {
              const on = picked.includes(f.name);
              const lock = locked.includes(f.name);
              return (
                <button type="button" key={f.name} onClick={() => toggle(f.name)} aria-pressed={on} aria-disabled={lock} data-testid={`calc-feature-${i}`}
                  className={`calc-opt ${on ? "on" : ""} ${lock ? "locked" : ""}`}>
                  <span className="calc-check">{lock ? <Lock size={11} strokeWidth={3} /> : on && <Check size={13} strokeWidth={3} />}</span>
                  <span className="flex-1 text-left min-w-0">
                    <span className="block text-sm font-medium">{f.name}</span>
                    {f.desc && <span className="block text-xs text-[#77736B] mt-1 leading-snug" data-testid={`calc-feature-desc-${i}`}>{f.desc}</span>}
                    {lock && <span className="block text-[11px] text-[#b58a3f] font-semibold mt-1.5" data-testid={`calc-feature-locked-${i}`}>Paketə daxildir</span>}
                  </span>
                  {free.includes(f.name) ? (
                    <span className="text-sm font-semibold whitespace-nowrap text-[#3d7a37]" data-testid={`calc-feature-free-${i}`}><s className="text-[#77736B] font-normal mr-1">+{fmt(f.price)} ₼</s>Pulsuz</span>
                  ) : <span className="text-sm font-semibold whitespace-nowrap">+{fmt(f.price)} ₼</span>}
                </button>
              );
            })}
          </div>
          <Extras list={c.extras} />
        </div>
      </div>
    </section>
  );
}
